/**
 * 商品分类字典（2026-09-14 品类数据化第一阶段：API 主源 + 内置 fallback）
 *
 * 架构：categories 表 = 经营者可维护业务数据（Admin CRUD），本模块是前端唯一字典入口——
 *   - 主源：GET /api/categories（active only，sort_order 序）模块级缓存，首次 import 预热
 *   - fallback：内置 CATEGORY_DICTIONARY（与 seed 逐字一致，60 类）
 *   - 解析链：API 缓存 → 内置字典 → slug 原值（未知 key 不丢，直接展示）
 * 与 specFieldMap.js 分离：
 *   - specFieldMap.js 只管"哪些分类有结构化规格字段 + 字段定义"（13 类）
 *   - 本字典管全部分类的 key→中文名（60 类，含 13 类重叠）
 * 两者 13 类重叠，name 以本字典为准；adhesive/helmet/seat 等无结构化规格的分类
 * 只进本字典，不进 specFieldMap。
 *
 * 消费方：
 *   - 分类选择器（PC ProductManage / 移动 AdminProductEdit）→ getCategoriesForSelector()
 *   - 展示型分类名（Cart/Order/Detailspage/ProductManage 的 categoryText）→ getCategoryName()
 *
 * 分类 key 对应 products.category（后端自由 TEXT，无字典表约束）。
 * hasSpec:true 表示该分类在 specFieldMap.js 有结构化规格字段。
 */

import axios from 'axios'

export const CATEGORY_DICTIONARY = {
  // === 有结构化规格（specFieldMap 13 类，name 与 SPEC_CATEGORY_META 对齐）===
  tire:      { name: '轮胎',   hasSpec: true },
  battery:   { name: '电池',   hasSpec: true },
  oil:       { name: '机油',   hasSpec: true },
  bearing:   { name: '轴承',   hasSpec: true },
  seal:      { name: '密封件', hasSpec: true },
  chain:     { name: '链条',   hasSpec: true },
  sprocket:  { name: '链轮',   hasSpec: true },
  spark_plug:{ name: '火花塞', hasSpec: true },
  belt:      { name: '皮带',   hasSpec: true },
  bulb:      { name: '灯泡',   hasSpec: true },
  brake_pad: { name: '刹车片', hasSpec: true },
  exhaust:   { name: '排气系统', hasSpec: true },
  fastener:  { name: '紧固件', hasSpec: true },

  // === 无结构化规格（规格走「规格」文本列）===
  tool:           { name: '工具',          hasSpec: false },
  mirror:         { name: '后视镜',        hasSpec: false },
  lock:           { name: '锁具',          hasSpec: false },
  ignition_lock:  { name: '电门锁',        hasSpec: false },
  clutch:         { name: '离合器',        hasSpec: false },
  cable:          { name: '拉索',               hasSpec: false },
  switch:         { name: '开关',          hasSpec: false },
  cylinder_kit:   { name: '套缸',          hasSpec: false },
  filter:         { name: '滤心',          hasSpec: false },
  bodywork:       { name: '车身覆盖件',    hasSpec: false },
  shock_absorber: { name: '减震',          hasSpec: false },
  carburetor:     { name: '化油器',        hasSpec: false },
  brake_pump:     { name: '刹车泵',        hasSpec: false },
  rocker_arm:     { name: '摇臂',          hasSpec: false },
  pulley:         { name: '皮带轮/驱动盘', hasSpec: false },
  starter_motor:  { name: '马达',          hasSpec: false },
  meter:          { name: '仪表',          hasSpec: false },
  piston:         { name: '活塞',          hasSpec: false },
  fuel_cap:       { name: '油箱盖',        hasSpec: false },
  valve:          { name: '气门',          hasSpec: false },
  cdi:            { name: '点火器',        hasSpec: false },
  coil:           { name: '线圈',          hasSpec: false },
  variator:       { name: '波盘',          hasSpec: false },
  rim:            { name: '钢圈',          hasSpec: false },
  charger:        { name: '充电器',        hasSpec: false },
  lever:          { name: '牛角',          hasSpec: false },
  regulator:      { name: '调压器',        hasSpec: false },
  camshaft:       { name: '凸轮轴',        hasSpec: false },
  rectifier:      { name: '整流器',        hasSpec: false },
  battery_part:   { name: '电瓶配件',      hasSpec: false },
  relay:          { name: '继电器',        hasSpec: false },
  ignition_coil:  { name: '高压包',        hasSpec: false },
  cylinder_head:  { name: '缸头',          hasSpec: false },
  universal_joint:{ name: '万向节',        hasSpec: false },
  horn:           { name: '喇叭',          hasSpec: false },
  crankshaft:     { name: '曲轴',          hasSpec: false },
  oil_pump:       { name: '机油泵',        hasSpec: false },

  // === misc 评估后新建（从"其他配件"按关键词分流而来）===
  adhesive:       { name: '胶粘剂',        hasSpec: false },
  helmet:         { name: '头盔',          hasSpec: false },
  seat:           { name: '座垫',          hasSpec: false },

  // === misc 二次清理新建（Phase2：密封垫/车灯总成/操控件/化学品/尾箱/防护）===
  lamp:           { name: '车灯总成',      hasSpec: false },
  control:        { name: '操控件',        hasSpec: false },
  chemicals:      { name: '养护化学品',    hasSpec: false },
  luggage:        { name: '尾箱',          hasSpec: false },
  protection:     { name: '防护用品',      hasSpec: false },

  // === Phase3 新建（碟刹盘/刹车鼓等制动零件；刹车片/刹车泵分立保留）===
  brake:          { name: '制动系统',      hasSpec: false },

  // === 兜底（不可识别的其他配件）===
  misc:           { name: '其他配件',      hasSpec: false }
}

// ===== API 主源层（categories 表 → GET /api/categories，active only）=====
// 模块级缓存（publicConfig 同款模式）：null=未加载，数组=已加载（含空数组）。
// 失败静默回退内置字典（name 与 seed 一致，无感降级）；编辑页等需要最新数据的
// 场景 await fetchCategoryDictionary() 后再取 options。
let apiCategories = null
let apiPending = null

export function fetchCategoryDictionary (force) {
  if (!force && apiCategories) return Promise.resolve(apiCategories)
  if (!force && apiPending) return apiPending
  apiPending = axios.get('/categories')
    .then(res => {
      const d = res.data && res.data.code === 200 && Array.isArray(res.data.data) ? res.data.data : []
      apiCategories = d.map(c => ({ value: c.slug, label: c.name, icon: c.icon || '' }))
      return apiCategories
    })
    .catch(() => {
      // 网络失败/后端不可用：保持 null，消费方回退内置字典（getCategoriesForSelector 内判）
      apiPending = null
      return null
    })
  return apiPending
}

// 模块首次 import 即预热（SPA 无 SSR 副作用）：App 启动后台拉一次，
// 首屏渲染赶不上时同步函数回退内置——两者 name 一致，用户无感
fetchCategoryDictionary()

// 分类 key → 中文名（展示型收敛统一入口；未知 key 回退原值，再回退"未分类"）
// 解析链：API 缓存（管理员改名即时生效）→ 内置字典 → slug 原值
export function getCategoryName (category) {
  if (!category) return '未分类'
  if (apiCategories) {
    const hit = apiCategories.find(c => c.value === category)
    if (hit) return hit.label
  }
  const entry = CATEGORY_DICTIONARY[category]
  return entry ? entry.name : category
}

// 分类 key → 元数据 {name, hasSpec}（无则 null）。hasSpec 是代码态规格能力（specFieldMap），
// API 不下发此信息，恒走内置字典
export function getCategoryMeta (category) {
  return CATEGORY_DICTIONARY[category] || null
}

// 全部分类 key 列表（API active 优先，未加载回内置）
export function getAllCategories () {
  if (apiCategories && apiCategories.length > 0) return apiCategories.map(c => c.value)
  return Object.keys(CATEGORY_DICTIONARY)
}

// 分类选择器选项（PC/移动端分类选择器统一入口）：[{value, label, icon?}]
// API active 品类优先（经营者维护+排序生效）；未加载/失败回内置字典
export function getCategoriesForSelector () {
  if (apiCategories && apiCategories.length > 0) return apiCategories
  return Object.keys(CATEGORY_DICTIONARY).map(key => ({
    value: key,
    label: CATEGORY_DICTIONARY[key].name
  }))
}

// 选择器选项是否已从 API 加载（编辑页判断是否需要追加"已停用"旧值项）
export function isCategoryDictionaryLoaded () {
  return apiCategories !== null
}
